import { useState } from 'react';
import { router } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Image data from the backend.
 * Supports hex, character, adventure, tome, and item media sources.
 */
interface ImageData {
    id: number;
    source_type: 'hex' | 'character' | 'adventure' | 'tome' | 'item';
    original_filename: string;
    mime_type: string;
    file_size: number;
    width: number | null;
    height: number | null;
    url_small: string;
    url_medium: string;
    url_large: string;
    created_at: string;
    uploader: {
        id: number;
        username: string;
    } | null;
    hex: {
        id: number;
        name: string;
    } | null;
    character: {
        id: number;
        name: string;
    } | null;
    adventure: {
        id: number;
        name: string;
    } | null;
    tome: {
        id: number;
        name: string;
        slug: string;
    } | null;
    item: {
        id: number;
        name: string;
        slug: string;
    } | null;
}

/**
 * Pagination links from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated response from Laravel.
 */
interface PaginatedImages {
    data: ImageData[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    links: PaginationLink[];
}

interface Props {
    images: PaginatedImages;
    filters: {
        user_id: string | null;
        source: 'hex' | 'character' | 'adventure' | 'tome' | 'item' | null;
    };
}

/**
 * Format file size in human-readable format.
 */
function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format date in a readable format.
 */
function formatDate(isoString: string): string {
    return new Date(isoString).toLocaleString();
}

/**
 * GM Image Moderation page - displays all uploaded images with moderation actions.
 * Supports both hex and character media.
 */
export default function Images({ images, filters }: Props) {
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    /**
     * Handle delete confirmation.
     * Uses different routes for hex, character, adventure, and item media.
     */
    const handleDelete = (image: ImageData) => {
        const deleteKey = `${image.source_type}-${image.id}`;
        setDeletingId(deleteKey);

        // Use the appropriate delete route based on source type
        let deleteUrl: string;
        switch (image.source_type) {
            case 'hex':
                deleteUrl = `/gm/images/hex/${image.id}`;
                break;
            case 'character':
                deleteUrl = `/gm/images/character/${image.id}`;
                break;
            case 'adventure':
                deleteUrl = `/gm/images/adventure/${image.id}`;
                break;
            case 'tome':
                deleteUrl = `/gm/images/tome/${image.id}`;
                break;
            case 'item':
                deleteUrl = `/gm/images/item/${image.id}`;
                break;
        }

        router.delete(deleteUrl, {
            preserveScroll: true,
            onFinish: () => {
                setDeletingId(null);
                setConfirmDeleteId(null);
            },
        });
    };

    // Handle filter by user
    const handleFilterByUser = (userId: number) => {
        router.get('/gm/images', { ...filters, user_id: userId }, { preserveState: true });
    };

    // Handle filter by source type
    const handleFilterBySource = (source: 'hex' | 'character' | 'adventure' | 'tome' | 'item' | null) => {
        const newFilters = { ...filters, source };
        if (!source) delete newFilters.source;
        router.get('/gm/images', newFilters, { preserveState: true });
    };

    // Clear all filters
    const handleClearFilters = () => {
        router.get('/gm/images', {}, { preserveState: true });
    };

    // Check if any filters are active
    const hasActiveFilters = filters.user_id || filters.source;

    return (
        <GmLayout title="Image Moderation" activePath="/gm/images">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">Image Moderation</h1>
                    <div className="text-gray-400">
                        {images.total} total image{images.total !== 1 ? 's' : ''}
                    </div>
                </div>

                {/* Source Filter Tabs */}
                <div className="mb-4 flex items-center gap-2">
                    <span className="text-gray-400 text-sm mr-2">Source:</span>
                    <button
                        onClick={() => handleFilterBySource(null)}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                            !filters.source
                                ? 'bg-purple-600 text-white'
                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                        }`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => handleFilterBySource('hex')}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                            filters.source === 'hex'
                                ? 'bg-purple-600 text-white'
                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                        }`}
                    >
                        🗺️ Hex
                    </button>
                    <button
                        onClick={() => handleFilterBySource('character')}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                            filters.source === 'character'
                                ? 'bg-purple-600 text-white'
                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                        }`}
                    >
                        👤 Character
                    </button>
                    <button
                        onClick={() => handleFilterBySource('adventure')}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                            filters.source === 'adventure'
                                ? 'bg-purple-600 text-white'
                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                        }`}
                    >
                        ⚔️ Adventure
                    </button>
                    <button
                        onClick={() => handleFilterBySource('tome')}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                            filters.source === 'tome'
                                ? 'bg-purple-600 text-white'
                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                        }`}
                    >
                        📖 Tome
                    </button>
                    <button
                        onClick={() => handleFilterBySource('item')}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                            filters.source === 'item'
                                ? 'bg-purple-600 text-white'
                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                        }`}
                    >
                        🎒 Item
                    </button>
                </div>

                {/* Active Filters */}
                {hasActiveFilters && (
                    <div className="mb-4 flex items-center gap-2 flex-wrap">
                        {filters.user_id && (
                            <span className="text-gray-400 text-sm">
                                User ID: {filters.user_id}
                            </span>
                        )}
                        <button
                            onClick={handleClearFilters}
                            className="text-purple-400 hover:text-purple-300 text-sm"
                        >
                            Clear all filters
                        </button>
                    </div>
                )}

                {/* Image Grid */}
                {images.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No images found.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {images.data.map((image) => {
                            // Create a unique key combining source type and id
                            const imageKey = `${image.source_type}-${image.id}`;
                            return (
                                <ImageCard
                                    key={imageKey}
                                    image={image}
                                    isDeleting={deletingId === imageKey}
                                    showConfirm={confirmDeleteId === imageKey}
                                    onConfirmDelete={() => setConfirmDeleteId(imageKey)}
                                    onCancelDelete={() => setConfirmDeleteId(null)}
                                    onDelete={() => handleDelete(image)}
                                    onFilterByUser={() => image.uploader && handleFilterByUser(image.uploader.id)}
                                />
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {images.last_page > 1 && (
                    <Pagination links={images.links} />
                )}
            </div>
        </GmLayout>
    );
}

/**
 * ImageCard - Displays a single image with metadata and actions.
 */
function ImageCard({
    image,
    isDeleting,
    showConfirm,
    onConfirmDelete,
    onCancelDelete,
    onDelete,
    onFilterByUser,
}: {
    image: ImageData;
    isDeleting: boolean;
    showConfirm: boolean;
    onConfirmDelete: () => void;
    onCancelDelete: () => void;
    onDelete: () => void;
    onFilterByUser: () => void;
}) {
    return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            {/* Image Preview - click to open full size in new tab */}
            <a
                href={image.url_large}
                target="_blank"
                rel="noopener noreferrer"
                className="aspect-square bg-slate-900 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
            >
                <img
                    src={image.url_medium}
                    alt={image.original_filename}
                    className="max-w-full max-h-full object-contain"
                />
            </a>

            {/* Image Info */}
            <div className="p-3">
                <p className="text-sm text-white truncate" title={image.original_filename}>
                    {image.original_filename}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                    {formatFileSize(image.file_size)}
                    {image.width && image.height && ` • ${image.width}×${image.height}`}
                </p>
                <p className="text-xs text-gray-400">
                    {formatDate(image.created_at)}
                </p>

                {/* Uploader Info */}
                {image.uploader && (
                    <p className="text-xs text-gray-400 mt-1">
                        By:{' '}
                        <button
                            onClick={onFilterByUser}
                            className="text-purple-400 hover:text-purple-300"
                        >
                            {image.uploader.username}
                        </button>
                        {' '}(ID: {image.uploader.id})
                    </p>
                )}

                {/* Source Info - Hex, Character, or Adventure */}
                {image.hex && (
                    <p className="text-xs text-gray-400">
                        <span className="text-blue-400">🗺️ Hex:</span>{' '}
                        <a
                            href={`/hex/${image.hex.id}`}
                            className="text-purple-400 hover:text-purple-300"
                        >
                            {image.hex.name}
                        </a>
                    </p>
                )}
                {image.character && (
                    <p className="text-xs text-gray-400">
                        <span className="text-green-400">👤 Character:</span>{' '}
                        <a
                            href={`/character/${image.character.id}`}
                            className="text-purple-400 hover:text-purple-300"
                        >
                            {image.character.name}
                        </a>
                    </p>
                )}
                {image.adventure && (
                    <p className="text-xs text-gray-400">
                        <span className="text-orange-400">⚔️ Adventure:</span>{' '}
                        <a
                            href={`/gm/adventures/${image.adventure.id}/edit`}
                            className="text-purple-400 hover:text-purple-300"
                        >
                            {image.adventure.name}
                        </a>
                    </p>
                )}
                {image.tome && (
                    <p className="text-xs text-gray-400">
                        <span className="text-purple-400">📖 Tome:</span>{' '}
                        <a
                            href={`/gm/tome/${image.tome.id}/edit`}
                            className="text-purple-400 hover:text-purple-300"
                        >
                            {image.tome.name}
                        </a>
                    </p>
                )}
                {image.item && (
                    <p className="text-xs text-gray-400">
                        <span className="text-yellow-400">🎒 Item:</span>{' '}
                        <a
                            href={`/gm/items/${image.item.id}/edit`}
                            className="text-purple-400 hover:text-purple-300"
                        >
                            {image.item.name}
                        </a>
                    </p>
                )}

                {/* Actions */}
                <div className="mt-3">
                    {showConfirm ? (
                        <div className="flex gap-2">
                            <button
                                onClick={onDelete}
                                disabled={isDeleting}
                                className="flex-1 px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white text-xs rounded transition-colors"
                            >
                                {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                            </button>
                            <button
                                onClick={onCancelDelete}
                                disabled={isDeleting}
                                className="flex-1 px-2 py-1 bg-slate-600 hover:bg-slate-500 text-white text-xs rounded transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={onConfirmDelete}
                            className="w-full px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                        >
                            🗑️ Delete Image
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

